/**
 * Customer app `durationUtils.formatDuration` — decimal hours to display string.
 */
export function formatConsumerDurationFromHours(hours: number): string {
  if (hours < 1) {
    const minutes = Math.round(hours * 60);
    return `${minutes} min`;
  }
  return `${hours.toFixed(2)} hrs`;
}

/**
 * Resolve total booking duration in hours.
 *
 * Matches customer app (`upcomingAppointments` / `yourLatestBooking`):
 * `real_no_of_hours ?? mainCleaner.real_no_of_hours ?? planned hours`.
 * Planned hours resolve from `duration_hours`+`additional_hours` (detail
 * endpoint), then `no_of_hours`/`booked_hours`, then `cleaners[0].no_of_hours`.
 */
export function consumerBookingDurationHours(booking: {
  real_no_of_hours?: number | null;
  duration_hours?: number | null;
  additional_hours?: number | null;
  no_of_hours?: number | null;
  booked_hours?: number | null;
  cleaners?: Array<
    | {
        no_of_hours?: number | null;
        real_no_of_hours?: number | null;
      }
    | null
    | undefined
  > | null;
}): number {
  const positive = (value: unknown): number | null => {
    if (value == null) return null;
    const n = Number(value);
    return Number.isFinite(n) && n > 0 ? n : null;
  };

  const main = booking.cleaners?.[0] ?? undefined;

  // Actual hours: booking-level, then main cleaner (JobProgress may finish
  // while the booking-level tracking window is incomplete).
  const real =
    positive(booking.real_no_of_hours) ?? positive(main?.real_no_of_hours);
  if (real != null) return real;

  // Planned hours: detail endpoint base + extensions.
  const base = positive(booking.duration_hours);
  if (base != null) {
    const extra = Number(booking.additional_hours);
    return base + (Number.isFinite(extra) && extra > 0 ? extra : 0);
  }

  // Planned hours: flat list fields, then cleaner assignment.
  const legacy =
    positive(booking.no_of_hours) ?? positive(booking.booked_hours);
  if (legacy != null) return legacy;

  return positive(main?.no_of_hours) ?? 0;
}
